import React, { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";

import NotificationsPanel from "@/components/organisms/NotificationsPanel";
import { useInAppNotifications } from "@/hooks/useInAppNotifications";
import { useInAppNotificationsStore } from "@/store/notificationsStore";
import { cn } from "@/utils";

/**
 * Колокольчик уведомлений: кнопка с unread-точкой + popover-список.
 *
 * Hook `useInAppNotifications` поднимает WS-канал и polling fallback.
 */
export const NotificationsBell: React.FC = () => {
  useInAppNotifications();

  const unreadCount = useInAppNotificationsStore((s) => s.unreadCount);
  const reload = useInAppNotificationsStore((s) => s.reload);

  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    void reload();
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [isOpen, reload]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-label="Уведомления"
        title="Уведомления"
        className={cn(
          "relative grid place-items-center w-9 h-9 rounded-full",
          "text-ink-2 hover:text-ink-1 hover:bg-surface-hover transition-colors",
          isOpen && "bg-surface-hover text-ink-1",
        )}
      >
        <Bell size={16} strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span
            className={cn(
              "absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1",
              // Один вид в обеих темах: светлая signal-бирюза и почти чёрные
              // цифры — на тёмной бирюзе светлой темы и со светлым текстом
              // счётчик не читался.
              "rounded-full bg-teal-400 text-on-accent text-[11px] font-bold leading-none tabular-nums",
              "grid place-items-center ring-[1.5px] ring-surface-canvas",
            )}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {isOpen && <NotificationsPanel onClose={() => setIsOpen(false)} />}
    </div>
  );
};

export default NotificationsBell;
